import React, { useState, useEffect } from 'react'
import { styled } from '@mui/material/styles'
import { Switch as MuiToggleSwitch, FormControlLabel } from '@mui/material'
import PropTypes from 'prop-types'
import ThemeConfig from './../../theme/ThemeConfig'
import * as ToggleSwitchTypes from './ToggleSwitchTypes'

const PREFIX = 'ToggleSwitch'

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  [`&.${classes.label}`]: {
    fontSize: ThemeConfig.typography.body1.fontSize,
    paddingBottom: 6,
  },
}))

const classes = {
  root: `${PREFIX}-root`,
  basicSwitchBase: `${PREFIX}-basicSwitchBase`,
  alertSwitchBase: `${PREFIX}-alertSwitchBase`,
  successSwitchBase: `${PREFIX}-successSwitchBase`,
  thumb: `${PREFIX}-thumb`,
  track: `${PREFIX}-track`,
  disabledTrack: `${PREFIX}-disabledTrack`,
  checked: `${PREFIX}-checked`,
  focusVisible: `${PREFIX}-focusVisible`,
}

const StyledMuiToggleSwitch = styled(MuiToggleSwitch)(({ theme }) => ({
  [`&.${classes.root}`]: {
    width: '44px',
    height: '30px',
    padding: 0,
    margin: theme.spacing(1),
    '&.MuiSwitch-colorSecondary.Mui-disabled': {
      '& $checked': {
        color: ThemeConfig.palette.background.paper,
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: ThemeConfig.palette.secondary.light,
        },
      },
      color: ThemeConfig.palette.secondary.light,
      '& + .MuiSwitch-track': {
        border: '2px solid ' + ThemeConfig.palette.defaultGrey.main,
        backgroundColor: ThemeConfig.palette.background.paper,
        opacity: 1,
      },
    },
  },

  [`& .${classes.basicSwitchBase}`]: {
    padding: '3px',
    color: activeToggleGray,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: ThemeConfig.palette.background.paper,
      '& + .MuiSwitch-track': {
        background: ThemeConfig.palette.primary.main,
        opacity: 1,
        border: '2px solid transparent',
      },
    },
  },

  [`& .${classes.alertSwitchBase}`]: {
    padding: '3px',
    color: activeToggleGray,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: ThemeConfig.palette.background.paper,
      '& + .MuiSwitch-track': {
        background: ThemeConfig.palette.warning.dark,
        opacity: 1,
        border: '2px solid transparent',
      },
    },
  },

  [`& .${classes.successSwitchBase}`]: {
    padding: '3px',
    color: activeToggleGray,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: ThemeConfig.palette.background.paper,
      '& + .MuiSwitch-track': {
        background: ThemeConfig.palette.success.main,
        opacity: 1,
        border: '2px solid transparent',
      },
    },
  },

  [`& .${classes.thumb}`]: {
    width: 18,
    height: 17,
  },

  [`& .${classes.track}`]: {
    borderRadius: trackBorderRadius,
    border: '2px solid ' + activeToggleGray,
    backgroundColor: theme.palette.grey[50],
    opacity: 1,
    transition: 'border .3s ease,background .3s ease',
    height: trackHeight,
    width: trackWidth,
  },

  [`& .${classes.disabledTrack}`]: {
    borderRadius: trackBorderRadius,
    opacity: 1,
    transition: 'border .3s ease,background .3s ease',
    height: trackHeight,
    width: trackWidth,
  },

  [`& .${classes.checked}`]: {},
  [`& .${classes.focusVisible}`]: {},
}))

const activeToggleGray = '#B3B4B3'
const trackHeight = 20
const trackWidth = 44
const trackBorderRadius = 22

const ToggleSwitch = props => {
  const {
    label,
    switchType,
    id,
    onClick,
    value,
    defaultValue,
    disabled,
    required,
    labelPlacement,
    ...others
  } = props
  let switchBaseClass = ''
  switch (switchType) {
    case ToggleSwitchTypes.SWITCH_TYPES.ALERT:
      switchBaseClass = classes.alertSwitchBase
      break
    case ToggleSwitchTypes.SWITCH_TYPES.BASIC:
      switchBaseClass = classes.basicSwitchBase
      break
    case ToggleSwitchTypes.SWITCH_TYPES.SUCCESS:
      switchBaseClass = classes.successSwitchBase
      break
    default:
      switchBaseClass = classes.basicSwitchBase
  }

  const [checked, setChecked] = useState(Boolean(defaultValue ?? value))

  useEffect(() => {
    if (value !== undefined && value !== null && Boolean(value) !== checked) {
      setChecked(Boolean(value))
    }
  }, [value, checked])

  const handleChange = event => {
    setChecked(event.target.checked)
    onClick(event.target.checked)
  }

  let trackStyleClass = classes.track
  if (disabled === true) {
    trackStyleClass = classes.disabledTrack
  }

  return (
    <StyledFormControlLabel
      control={
        <StyledMuiToggleSwitch
          inputProps={{ 'aria-label': id }}
          checked={checked}
          onChange={handleChange}
          value="checkedSwitch"
          id={id}
          disabled={disabled}
          required={required}
          classes={{
            root: classes.root,
            switchBase: switchBaseClass,
            thumb: classes.thumb,
            track: trackStyleClass,
            checked: classes.checked,
          }}
          {...others}
        />
      }
      label={label}
      labelPlacement={labelPlacement}
    />
  )
}

ToggleSwitch.propTypes = {
  label: PropTypes.string,
  id: PropTypes.string.isRequired,
  defaultValue: PropTypes.bool,
  required: PropTypes.bool,
  switchType: PropTypes.string,
  value: PropTypes.bool,
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  labelPlacement: PropTypes.string,
}

export default ToggleSwitch
